/**
 * The only module that talks to Telegram's Mini App runtime (telegram-web-app.js). Everything else uses this bridge,
 * which also provides browser fallbacks so the app runs (and is tested) outside Telegram.
 */

type WebApp = typeof window.Telegram.WebApp;

const LAUNCH_FLAG = 'spin:tg-launch';
let webApp: WebApp | null = null;

function telegramWindow(): { Telegram?: { WebApp?: WebApp } } {
  return window as unknown as { Telegram?: { WebApp?: WebApp } };
}

/** True when Telegram opened us (launch params in the hash, or a reload of such a launch). */
export function isTelegramLaunch(): boolean {
  return (
    /tgWebAppData=/.test(window.location.hash) ||
    sessionStorage.getItem(LAUNCH_FLAG) === '1' ||
    'TelegramWebviewProxy' in window
  );
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = false;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`failed to load ${src}`));
    document.head.appendChild(s);
  });
}

/** The game starts at the wooden table; the portal pages repaint the bars with tg.setColors. */
const THEME_COLORS = { header: '#b8743b', background: '#f5f5f5' } as const;

/** Loads telegram-web-app.js only for real Telegram launches and prepares the viewport. */
export async function initTelegram(): Promise<WebApp | null> {
  if (!isTelegramLaunch()) return null;
  sessionStorage.setItem(LAUNCH_FLAG, '1');
  if (!telegramWindow().Telegram?.WebApp) await loadScript('https://telegram.org/js/telegram-web-app.js?59');
  webApp = telegramWindow().Telegram?.WebApp ?? null;
  if (!webApp) return null;
  webApp.ready();
  webApp.expand();
  if (webApp.isVersionAtLeast('7.7')) webApp.disableVerticalSwipes();
  // The Mini App uses its own colours whatever Telegram's theme is; paint the header and bars to match.
  if (webApp.isVersionAtLeast('6.9')) webApp.setHeaderColor(THEME_COLORS.header);
  else if (webApp.isVersionAtLeast('6.1')) webApp.setHeaderColor('bg_color');
  if (webApp.isVersionAtLeast('6.1')) webApp.setBackgroundColor(THEME_COLORS.background);
  if (webApp.isVersionAtLeast('7.10')) webApp.setBottomBarColor(THEME_COLORS.background);
  return webApp;
}

export const tg = {
  get inTelegram(): boolean {
    return Boolean(webApp?.initData);
  },
  initData(): string {
    return webApp?.initData ?? '';
  },
  languageCode(): string | undefined {
    return webApp?.initDataUnsafe.user?.language_code ?? navigator.language;
  },
  startParam(): string | null {
    return webApp?.initDataUnsafe.start_param ?? null;
  },
  colorScheme(): 'light' | 'dark' {
    if (webApp) return webApp.colorScheme;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  },
  onThemeChanged(fn: () => void): () => void {
    webApp?.onEvent('themeChanged', fn);
    return () => webApp?.offEvent('themeChanged', fn);
  },

  backButton: {
    show(onClick: () => void): () => void {
      if (!webApp?.isVersionAtLeast('6.1')) return () => undefined;
      webApp.BackButton.onClick(onClick);
      webApp.BackButton.show();
      return () => {
        webApp?.BackButton.offClick(onClick);
        webApp?.BackButton.hide();
      };
    },
  },

  haptic(kind: 'light' | 'medium' | 'success' | 'error' | 'select'): void {
    const h = webApp?.isVersionAtLeast('6.1') ? webApp.HapticFeedback : null;
    if (h) {
      if (kind === 'success' || kind === 'error') h.notificationOccurred(kind);
      else if (kind === 'select') h.selectionChanged();
      else h.impactOccurred(kind);
      return;
    }
    navigator.vibrate?.(kind === 'error' ? [30, 40, 30] : 20);
  },

  /** Asks before closing the Mini App while seated at a table. */
  confirmClose(enabled: boolean): void {
    if (!webApp?.isVersionAtLeast('6.2')) return;
    if (enabled) webApp.enableClosingConfirmation();
    else webApp.disableClosingConfirmation();
  },

  /** Opens Telegram's share sheet for an invite link (only ever on a user tap). */
  share(url: string, text: string): void {
    const link = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
    if (webApp) webApp.openTelegramLink(link);
    else if (navigator.share) void navigator.share({ url, text }).catch(() => undefined);
    else window.open(link, '_blank', 'noopener');
  },

  /** Opens a t.me link inside Telegram (or any other link in the browser). */
  openTelegramUrl(url: string): void {
    if (webApp && url.startsWith('https://t.me/')) webApp.openTelegramLink(url);
    else if (webApp) webApp.openLink(url);
    else window.open(url, '_blank', 'noopener');
  },

  openBotChat(botUsername: string, command?: string): void {
    const link = `https://t.me/${botUsername}${command ? `?start=${encodeURIComponent(command)}` : ''}`;
    if (webApp) webApp.openTelegramLink(link);
    else window.open(link, '_blank', 'noopener');
  },

  /** Paints Telegram's header and background (the game is light, the portal dark). */
  setColors(header: string): void {
    if (!webApp?.isVersionAtLeast('6.9')) return;
    webApp.setHeaderColor(header as `#${string}`);
    webApp.setBackgroundColor(header as `#${string}`);
  },

  /** Closes the Mini App (the portal's Logout inside Telegram). */
  close(): void {
    webApp?.close();
  },
};
